/**
 * TimezoneDate
 * Class for Date representation in different timezone.
 * It shows date/time in local timezone as it shown in specified timezone (for example tenant timezone)
 * But for UTC date/time it will return real values for specified timezone
 * And for the simple dates (no time set) it will show both utc and local dates the same without any offsets
 */
export class TimezoneDate implements Date {
    private constructor(
        private readonly localDate: Date,
        private readonly utcDate: Date,
        private readonly tzOffsetUTC: number
    ) {}

    static getOffsetFromTimezone(ianaTZCode: string, dateTime?: Date): number {
        const dt = dateTime ? new Date(dateTime.getTime()) : new Date();
        dt.setSeconds(0, 0);

        // Format current time in `ianaCode` as `M/DD/YYYY, HH:MM:SS`:
        const tzDateString = dt.toLocaleString('en-US', {
            timeZone: ianaTZCode,
            hourCycle: 'h23',
        });

        // Parse formatted date string:
        const match = /(\d+)\/(\d+)\/(\d+), (\d+):(\d+)/.exec(tzDateString);
        if (!match) {
            return -dt.getTimezoneOffset();
        }

        const [, month, day, year, hour, min] = match.map(Number);

        // Change date string's time zone to UTC and get timestamp:
        const tzTime = Date.UTC(year, month - 1, day, hour, min);

        // Return the offset between UTC and target time zone:
        return Math.floor((tzTime - dt.getTime()) / (1000 * 60));
    }

    /**
     * create TimezoneDate without time from iso string. UTC date is used as base date
     * use it when date comes from backend
     */
    static dateFromIsoString(isoString: string): TimezoneDate {
        return TimezoneDate.dateFromDateUTC(new Date(isoString));
    }

    /**
     * create TimezoneDate without time from js date. UTC date is used as base date
     * use it when date comes from backend
     */
    static dateFromDateUTC(dt: Date): TimezoneDate {
        return new TimezoneDate(
            new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()),
            dt,
            0
        );
    }

    /**
     * create TimezoneDate without time from js date. local date is used as base date (it is converted to specified timezone)
     * use it when date comes from frontend
     */
    static dateFromDateLocal(dt: Date): TimezoneDate {
        return new TimezoneDate(
            dt,
            new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate())),
            0
        );
    }

    /**
     * create TimezoneDate with time from iso string. UTC date is used as base date
     * use it when date comes from backend
     */
    static fromIsoString(ianaTZCode: string, isoString: string): TimezoneDate {
        return TimezoneDate.fromDateUTC(ianaTZCode, new Date(isoString));
    }

    /**
     * create TimezoneDate with time from js date. UTC date is used as base date
     * use it when date comes from backend
     */
    static fromDateUTC(ianaTZCode: string, dt: Date): TimezoneDate {
        const tzOffsetUTC = TimezoneDate.getOffsetFromTimezone(ianaTZCode, dt);
        const diffOffset = -dt.getTimezoneOffset() - tzOffsetUTC;

        return new TimezoneDate(new Date(dt.getTime() - diffOffset * 60000), dt, tzOffsetUTC);
    }

    /**
     * create TimezoneDate without time from js date. local date is used as base date (it is converted to specified timezone)
     * use it when date comes from frontend
     */
    static fromDateLocal(ianaTZCode: string, dt: Date): TimezoneDate {
        const tzOffsetUTC = TimezoneDate.getOffsetFromTimezone(ianaTZCode, dt);
        const diffOffset = -dt.getTimezoneOffset() - tzOffsetUTC;

        return new TimezoneDate(dt, new Date(dt.getTime() + diffOffset * 60000), tzOffsetUTC);
    }

    get local() {
        return new Date(this.localDate);
    }

    get utc() {
        return new Date(this.utcDate);
    }

    toString(): string {
        return this.localDate.toString();
    }
    toLocaleString(): string;
    toLocaleString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string;
    toLocaleString(locales?: any, options?: any): string {
        return this.localDate.toLocaleString(locales, options);
    }
    valueOf(): number {
        return this.localDate.valueOf();
    }

    [Symbol.toPrimitive](hint: 'default'): string;
    [Symbol.toPrimitive](hint: 'string'): string;
    [Symbol.toPrimitive](hint: 'number'): number;
    [Symbol.toPrimitive](hint: string): string | number;
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    [Symbol.toPrimitive](hint: 'default' | 'string' | 'number' | string): string | number {
        /**
         * since TimezoneDate is needed for local date presentation, it will use localDate for all kind of compare
         * if you want to compare by utc date, use utc property, which is real utc date
         */
        return this.localDate[Symbol.toPrimitive](hint);
    }

    getDate(): number {
        return this.localDate.getDate();
    }

    getDay(): number {
        return this.localDate.getDay();
    }

    getFullYear(): number {
        return this.localDate.getFullYear();
    }

    getHours(): number {
        return this.localDate.getHours();
    }

    getMilliseconds(): number {
        return this.localDate.getMilliseconds();
    }

    getMinutes(): number {
        return this.localDate.getMinutes();
    }

    getMonth(): number {
        return this.localDate.getMonth();
    }

    getSeconds(): number {
        return this.localDate.getSeconds();
    }

    getTime(): number {
        return this.localDate.getTime();
    }

    getTimezoneOffset(): number {
        return -this.tzOffsetUTC;
    }

    getUTCDate(): number {
        return this.utcDate.getUTCDate();
    }

    getUTCDay(): number {
        return this.utcDate.getUTCDay();
    }

    getUTCFullYear(): number {
        return this.utcDate.getUTCFullYear();
    }

    getUTCHours(): number {
        return this.utcDate.getUTCHours();
    }

    getUTCMilliseconds(): number {
        return this.utcDate.getUTCMilliseconds();
    }

    getUTCMinutes(): number {
        return this.utcDate.getUTCMinutes();
    }

    getUTCMonth(): number {
        return this.localDate.getUTCMonth();
    }

    getUTCSeconds(): number {
        return this.utcDate.getUTCSeconds();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setDate(date: number): number {
        return this.localDate.getDate();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setFullYear(year: number, month?: number, date?: number): number {
        return this.localDate.getFullYear();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this.localDate.getHours();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMilliseconds(ms: number): number {
        return this.localDate.getMilliseconds();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMinutes(min: number, sec?: number, ms?: number): number {
        return this.localDate.getMinutes();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMonth(month: number, date?: number): number {
        return this.localDate.getMonth();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setSeconds(sec: number, ms?: number): number {
        return this.localDate.getSeconds();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setTime(time: number): number {
        return this.localDate.getTime();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCDate(date: number): number {
        return this.utcDate.getUTCDate();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCFullYear(year: number, month?: number, date?: number): number {
        return this.utcDate.getUTCFullYear();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCHours(hours: number, min?: number, sec?: number, ms?: number): number {
        return this.utcDate.getUTCHours();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCMilliseconds(ms: number): number {
        return this.utcDate.getUTCMilliseconds();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCMinutes(min: number, sec?: number, ms?: number): number {
        return this.utcDate.getUTCMinutes();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCMonth(month: number, date?: number): number {
        return this.utcDate.getUTCMonth();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setUTCSeconds(sec: number, ms?: number): number {
        return this.utcDate.getUTCSeconds();
    }

    toDateString(): string {
        return this.localDate.toDateString();
    }

    toISOString(): string {
        return this.utcDate.toISOString();
    }

    toJSON(key?: any): string {
        return this.utcDate.toJSON(key);
    }

    toLocaleDateString(): string;
    toLocaleDateString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string;
    toLocaleDateString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
        return this.localDate.toLocaleDateString(locales, options);
    }

    toLocaleTimeString(): string;
    toLocaleTimeString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string;
    toLocaleTimeString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
        return this.localDate.toLocaleTimeString(locales, options);
    }

    toTimeString(): string {
        return this.localDate.toTimeString();
    }

    toUTCString(): string {
        return this.utcDate.toUTCString();
    }

    toTemporalInstant(): Temporal.Instant {
        return this.localDate.toTemporalInstant();
    }
}
